<template>
  <div :class="$forms.authWindow">

    <app-iframe-dialog :value="termsAndServiceDialog"
                       iframe-src="https://eztx.com/eztx-terms-and-service/"
                       @close="closeTermsAndService" />

    <app-iframe-dialog :value="privacyPolicyDialog"
                       iframe-src="https://eztx.com/eztx-inc-digital-privacy-notice/"
                       @close="closePrivacyPolicy" />

    <div v-if="!registered">
      <div :class="$forms.authLogo">
        <app-logo />
      </div>

      <div v-if="serverError"
           :class="$forms.errorBox">
        <p>{{ serverError }}</p>
      </div>

      <div v-if="$v.user.firstName.$error"
           :class="$forms.errorBox">
        <p>
          First Name is required
        </p>
      </div>

      <div v-if="$v.user.lastName.$error"
           :class="$forms.errorBox">
        <p>
          Last Name is required
        </p>
      </div>

      <div v-if="$v.user.email.$error"
           :class="$forms.errorBox">
        <p>
          Email is invalid
        </p>
      </div>

      <div v-if="isPasswordRequired"
           :class="$forms.errorBox">
        <p>
          Password is required
        </p>
      </div>

      <div v-if="isPasswordInvalid"
           :class="$forms.errorBox">
        <p>
          Password must be at least 6 characters and contain at least one uppercase letter,
          one lowercase letter, one digit and one symbol.
        </p>
      </div>

      <form :class="$style.signupForm"
            @submit.prevent="signUp">

        <h2>Sign Up</h2>

        <div>
          <app-input-group v-model="$v.user.firstName.$model"
                           label="First Name*"
                           :class="getFirstNameClass"
                           :maxlength="255"
                           type="text"/>
        </div>

        <div>
          <app-input-group v-model="$v.user.lastName.$model"
                           label="Last Name*"
                           :class="getLastNameClass"
                           :maxlength="255"
                           type="text"/>
        </div>

        <div>
          <app-input-group v-model="$v.user.email.$model"
                           label="Email*"
                           :class="getEmailClass"
                           :maxlength="255"
                           type="email"/>
        </div>

        <div>
          <app-password-field v-model="$v.user.plainPassword.$model"
                              :class="getPasswordClass"
                              :maxlength="255"
                              label="Password*"/>
        </div>

        <div v-for="question of securityQuestions"
             :key="question.id">
          <div>
            <app-input-group :maxlength="255"
                             type="text"
                             :label="getSecurityQuestion(question.question)"
                             @input="setAnswerValue($event, question.id)"/>
          </div>
        </div>

        <div>
          <app-checkbox-group label="Send me updates"
                              v-model="user.sendMeUpdates"
                              :input-value="true"
                              :value="getSendUpdates"
                              @input="toggleSendUpdates">
          </app-checkbox-group>
        </div>

        <div>
          <app-checkbox-group v-model="$v.user.agreement.$model"
                              :value="getAgreement"
                              @input="toggleAgreement">
            <template v-slot:label>
             <p :class="$style.agreementLabel">
               I agree to the <a @click.prevent.stop="openTermsAndService">
               Terms and Conditions</a> and
               <a @click.prevent.stop="openPrivacyPolicy">
               Privacy Policy
               </a>
             </p>
            </template>
          </app-checkbox-group>
        </div>

        <app-button :class="$forms.authSubmitBtn"
                    button-style="accent"
                    type="submit"
                    text="Sign Up"
                    :disabled="isFormInvalid"/>

        <p :class="$forms.notice">
          Already have an account?
          <router-link :to="{ name: 'login' }"
                       :class="$forms.authLink">
            Sign In
          </router-link>
        </p>

      </form>
    </div>

    <app-auth-message v-else
                      :message="message"/>

  </div>
</template>

<script>
import currency from 'currency.js';
import { required, email } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';
import { passwordValidation, mustBeTrue } from '@/utils/validation';
import { QUESTION_TYPES } from '@/constants';
import { uuid } from 'vue-uuid';

export default {
  data() {
    return {
      user: {
        firstName: '',
        lastName: '',
        email: '',
        plainPassword: '',
        preselectedAppAccessProductId: 1,
        sendMeUpdates: true,
        agreement: false,
        securityQuestionAnswers: [],
        clientMachineToken: '',
      },
      registered: false,
      message: '',
      serverError: '',
      securityQuestions: [],
      termsAndServiceDialog: false,
      privacyPolicyDialog: false,
    };
  },
  methods: {
    ...mapActions([
      'register',
      'fetchSecurityQuestions',
    ]),
    signUp() {
      const token = uuid.v4();

      const user = {
        ...this.user,
        sendMeUpdates: +this.user.sendMeUpdates,
        clientMachineToken: token,
      };
      this.register(user)
        .then(() => {
          this.registered = true;
          this.message = 'We sent a message to the email address you indicated. Please check that email to confirm registration.';
        })
        .catch((err) => {
          this.handleAuthErrors(err);
        });
    },
    handleAuthErrors(err) {
      if (err.response.status === 404) {
        this.serverError = 'Not found';
      } else if (err.response.status === 400) {
        this.serverError = 'This email is already taken';
      } else if (err.response.status.toString().startsWith('5')) {
        this.serverError = 'Internal Server Error';
      } else {
        this.serverError = err;
      }
    },
    /* Change the value of the checkbox input */
    toggleSendUpdates(val) {
      this.user.sendMeUpdates = !!val;
    },
    toggleAgreement(val) {
      this.user.agreement = !!val;
    },
    setAnswerValue(e, id) {
      const currentAnswer = this.user.securityQuestionAnswers
        .find(answer => answer.securityQuestionId === id);
      currentAnswer.answer = e.trim();
    },
    getSecurityQuestion(question) {
      return `${question}*`;
    },
    openTermsAndService() {
      this.termsAndServiceDialog = true;
    },
    closeTermsAndService() {
      this.termsAndServiceDialog = false;
    },
    openPrivacyPolicy() {
      this.privacyPolicyDialog = true;
    },
    closePrivacyPolicy() {
      this.privacyPolicyDialog = false;
    },
  },
  computed: {
    getFirstNameClass() {
      if (this.$v.user.firstName.$error) {
        return this.$forms.errorInput;
      }

      return false;
    },
    getLastNameClass() {
      if (this.$v.user.lastName.$error) {
        return this.$forms.errorInput;
      }

      return false;
    },
    getEmailClass() {
      if (this.$v.user.email.$error) {
        return this.$forms.errorInput;
      }

      return false;
    },
    getPasswordClass() {
      if (this.$v.user.plainPassword.$error) {
        return this.$forms.errorInput;
      }

      return false;
    },
    isPasswordRequired() {
      return this.$v.user.plainPassword.$error
        && !this.$v.user.plainPassword.required;
    },
    isPasswordInvalid() {
      return this.$v.user.plainPassword.$error
        && !this.$v.user.plainPassword.passwordValidation;
    },
    /* Get the current value of the checkbox input */
    getSendUpdates() {
      return this.user.sendMeUpdates;
    },
    getAgreement() {
      return this.user.agreement;
    },
    isFormInvalid() {
      const answersNotFilled = this.user.securityQuestionAnswers.some(answer => answer.answer.trim() === '');
      return this.$v.$invalid || answersNotFilled;
    },
  },
  created() {
    this.user.preselectedAppAccessProductId = currency(this.$route.query.appAccessType).value
      || this.user.preselectedAppAccessProductId;
    this.fetchSecurityQuestions()
      .then((res) => {
        this.securityQuestions = res.data
          .filter(question => question.questionType === QUESTION_TYPES.USER_TYPE);
        this.user.securityQuestionAnswers = this.securityQuestions.map(question => ({
          securityQuestionId: question.id,
          answer: '',
        }));
      })
      .catch((err) => {
        this.serverError = err.message;
      });
  },
  validations: {
    user: {
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      email: {
        required,
        email,
      },
      plainPassword: {
        required,
        passwordValidation,
      },
      agreement: {
        mustBeTrue,
      },
    },
  },
};
</script>

<style src="@/styles/forms.scss" lang="scss" module="$forms"></style>
<style lang="scss" module>
@import "@/styles/variables.scss";

.signupForm {
  padding: 40px 60px 34px;
  text-align: center;

  .agreementLabel {
    margin-bottom: 0 ;
  }
}

</style>
